import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Assignment, CalculationPriceRule } from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { CardComponent } from '@ui/card/card.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-assignments-price-rules',
  standalone: true,
  imports: [CommonModule, CardComponent],
  template: `
    @if (embedded()) {
      <div class="mt-4">
        <div class="text-sm font-semibold text-gray-700 mb-3">Voorcalculatie</div>
        <ng-container [ngTemplateOutlet]="priceRulesContent"></ng-container>
      </div>
    } @else {
      <ui-card [title]="'Voorcalculatie'">
        <ng-container [ngTemplateOutlet]="priceRulesContent"></ng-container>
      </ui-card>
    }

    <ng-template #priceRulesContent>
      @if (!hasWmoReference()) {
        <p class="text-sm text-gray-500 py-2">
          Geen WMO-referentie — voorcalculatie niet beschikbaar
        </p>
      } @else if (loading()) {
        <div class="space-y-3">
          <div class="h-4 bg-slate-200 rounded animate-pulse w-3/4"></div>
          <div class="h-4 bg-slate-200 rounded animate-pulse w-1/2"></div>
        </div>
      } @else if (priceRules().length) {
        @for (rule of priceRules(); track $index) {
          <div class="py-3 border-b border-gray-100 last:border-0">
            <div class="text-sm font-medium">
              {{ rule.item_omschrijving ?? '—' }}
            </div>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 mt-1 text-xs text-gray-600"
            >
              <span>
                Aantal:
                <span class="font-medium text-gray-900">{{
                  rule.aantal_eenheden ?? '—'
                }}</span>
              </span>
              <span class="sm:text-right">
              Prijs per eenheid (incl. BTW):
                <span class="font-medium text-gray-900">{{
                          rule.prijs_per_eenheid != null
                    ? (rule.prijs_per_eenheid | currency : 'EUR')
                    : '—'
                }}</span>
              </span>
            </div>
          </div>
        }
      } @else {
        <p class="text-sm text-gray-500 py-2">Geen prijsregels gevonden</p>
      }
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class AssignmentsPriceRulesComponent {
  private assignmentsService = inject(AssignmentsService);
  private cdr = inject(ChangeDetectorRef);

  assignment = input.required<Assignment | null>();
  embedded = input(false);

  priceRules = signal<CalculationPriceRule[]>([]);
  loading = signal(false);

  hasWmoReference = () => !!this.assignment()?.wmo_reference?.trim();

  constructor() {
    effect(
      () => {
        const assignmentId = this.assignment()?.id;
        if (assignmentId && this.hasWmoReference()) {
          this.getPriceRules(assignmentId);
        } else {
          this.priceRules.set([]);
          this.loading.set(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  getPriceRules(assignmentId: string) {
    this.loading.set(true);
    this.assignmentsService
      .getCalculationPriceRules(assignmentId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.priceRules.set(response.price_rules ?? []);
          this.cdr.markForCheck();
        },
        error: () => {
          this.priceRules.set([]);
          this.cdr.markForCheck();
        },
      });
  }
}
